import { create } from 'zustand';

import type { CandidateDetailsTabValue, CandidateViewModel } from '@/entities/candidate';

export enum ChatPanelState {
  JobPreview = 'jobPreview',
  BestMatches = 'bestMatches',
  Closed = 'closed',
}

type ChatStore = {
  chatPanelState: ChatPanelState;
  selectedCandidate: CandidateViewModel | null;
  initialTab: CandidateDetailsTabValue | undefined;

  setChatPanelState: (state: ChatPanelState) => void;
  setSelectedCandidate: (candidate: CandidateViewModel | null) => void;
  setSelectedCandidateWithTab: (candidate: CandidateViewModel | null, tab?: CandidateDetailsTabValue) => void;
  reset: () => void;
};

const initialState = {
  chatPanelState: ChatPanelState.Closed,
  selectedCandidate: null,
  initialTab: undefined,
};

export const useChatStore = create<ChatStore>((set) => ({
  ...initialState,
  setChatPanelState: (state: ChatPanelState) =>
    set({
      chatPanelState: state,
      ...(state === ChatPanelState.Closed ? { selectedCandidate: null, initialTab: undefined } : {}),
    }),
  setSelectedCandidate: (candidate: CandidateViewModel | null) =>
    set({ selectedCandidate: candidate, initialTab: undefined }),
  setSelectedCandidateWithTab: (candidate: CandidateViewModel | null, tab?: CandidateDetailsTabValue) =>
    set({ selectedCandidate: candidate, initialTab: tab }),
  reset: () => set(initialState),
}));

export const useChatActions = () => {
  const setChatPanelState = useChatStore((state) => state.setChatPanelState);
  const setSelectedCandidate = useChatStore((state) => state.setSelectedCandidate);
  const setSelectedCandidateWithTab = useChatStore((state) => state.setSelectedCandidateWithTab);
  const reset = useChatStore((state) => state.reset);

  return { setChatPanelState, setSelectedCandidate, setSelectedCandidateWithTab, reset };
};

export const useChatSelectedCandidate = () => {
  return useChatStore((state) => state.selectedCandidate);
};

export const useChatPanelState = () => {
  return useChatStore((state) => state.chatPanelState);
};

export const useChatInitialTab = () => {
  return useChatStore((state) => state.initialTab);
};
